import React, { useEffect, useState } from "react";
import { X, Check, Loader2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { createLead, ghlInit } from "../api";
import { LEGAL_LINKS } from "../mock";
import { hasConsent as metaHasConsent, browserIds as metaBrowserIds, lead as metaLead } from "../lib/meta";

const EMPTY = { name: "", phone: "", email: "", message: "", channel: "Telefono", vehicle: "", year: "", km: "", company: "", city: "", viewingTime: "", payment: "", tradeIn: "" };
// Immediate paid deposit disabled: booking is now a "richiesta di blocco".
// Carmaps verifies availability/conditions before proposing the reservation.
const RESERVATIONS_ENABLED = false;
const PRIVACY_URL = (LEGAL_LINKS[0] && LEGAL_LINKS[0].url) || "#";

const carRef = (car) => (car ? `${car.brand} ${car.trim} · Rif. ${car.ref}` : null);

function dialogTitle(mode) {
  if (mode === "sell") return "Vendi la tua auto";
  if (mode === "booking") return "Richiedi il blocco del veicolo";
  return "Richiedi informazioni";
}
function dialogEyebrow(mode) {
  if (mode === "sell") return "02 — Vendi con Carmaps";
  if (mode === "booking") return "Prenotazione";
  return "Contatto";
}
function dialogSubtitle(mode, car) {
  if (mode === "sell") return "Lasciaci i tuoi dati: ti ricontattiamo per la valutazione e il supporto alla vendita.";
  if (mode === "booking") return carRef(car) || "Verifichiamo la disponibilità e ti ricontattiamo.";
  return carRef(car) || "Lasciaci i tuoi dati: ti rispondiamo al più presto.";
}
function successMessage(mode, firstName) {
  if (mode === "booking")
    return "Richiesta ricevuta. Il veicolo non è ancora bloccato. Un incaricato Carmaps verificherà la disponibilità e ti contatterà per confermare condizioni e pagamento.";
  return `Grazie ${firstName}! Abbiamo registrato la tua richiesta e ti ricontatteremo a breve. Per urgenze chiama lo 011 283514.`;
}

// mode: "info" | "sell" | "booking". car (optional) for info/booking leads.
export default function LeadForm({ open, onClose, mode = "info", car = null }) {
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");
  const [submissionId, setSubmissionId] = useState("");
  const paidBooking = mode === "booking" && RESERVATIONS_ENABLED;

  useEffect(() => {
    if (open) {
      setStatus("idle"); setErrMsg(""); setForm(EMPTY); setAccepted(false);
      // stable id per submission: reused across retries, new one per open
      const uid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setSubmissionId(uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when dialog opens
  }, [open, mode, car]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return; // prevent double submit
    if (!form.name.trim()) { setErrMsg("Inserisci il tuo nome"); return; }
    if (!form.phone.trim() && !form.email.trim()) { setErrMsg("Inserisci un recapito (telefono o email)"); return; }
    if (mode === "sell" && !form.vehicle.trim()) { setErrMsg("Indica marca e modello dell'auto"); return; }
    if (paidBooking && !form.email.trim()) { setErrMsg("Per la prenotazione serve l'email (ricevuta pagamento)"); return; }
    if (paidBooking && !accepted) { setErrMsg("Devi accettare le condizioni di prenotazione"); return; }
    setErrMsg("");
    setStatus("loading");
    const payload = {
      type: mode,
      submission_id: submissionId,
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      message: form.message.trim() || null,
      company: form.company || null, // honeypot
      source: typeof window !== "undefined" ? window.location.pathname : null,
    };
    if (mode === "sell") {
      payload.vehicle = form.vehicle.trim();
      payload.year = form.year.trim() || null;
      payload.km = form.km.trim() || null;
    } else if (car) {
      payload.car_title = `${car.brand} ${car.trim}`.trim();
      payload.car_handle = car.handle;
      payload.car_ref = car.ref;
      payload.car_url = `${window.location.origin}/auto/${car.handle}`;
      payload.shopify_product_id = car.shopify_id != null ? String(car.shopify_id) : null;
      if (mode === "info") payload.channel = form.channel;
      if (mode === "booking") {
        payload.city = form.city.trim() || null;
        payload.viewing_time = form.viewingTime.trim() || null;
        payload.payment_mode = form.payment || null;
        payload.trade_in = form.tradeIn || null;
      }
      if (paidBooking) payload.accepted_conditions = true;
    }
    // Meta tracking context for server-side CAPI dedup (token stays server-side).
    const _consent = metaHasConsent();
    const _priceNum = car ? (parseInt(String(car.price || "").replace(/[^0-9]/g, ""), 10) || 0) : 0;
    payload.meta_consent = _consent;
    payload.event_source_url = typeof window !== "undefined" ? window.location.href : null;
    if (_consent) {
      const _fb = metaBrowserIds();
      payload.fbp = _fb.fbp || null;
      payload.fbc = _fb.fbc || null;
    }
    if (car && mode !== "sell") payload.car_value = _priceNum || null;
    try {
      await createLead(payload);
      if (paidBooking && car) {
        const reservePayload = {
          origin_url: window.location.origin,
          car_handle: car.handle,
          car_ref: car.ref,
          car_title: `${car.brand} ${car.trim}`.trim(),
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
        };
        // Richiesta di blocco gestita dal CRM (GoHighLevel).
        try {
          const g = await ghlInit(reservePayload);
          try { localStorage.setItem("cm_reservation", g.session_id); } catch (e) { console.warn("localStorage non disponibile:", e); }
          window.location.href = g.checkout_url;
          return;
        } catch (gErr) {
          setStatus("error");
          if (gErr?.response?.status === 409) {
            setErrMsg("Questa auto è già prenotata. Contattaci per la disponibilità.");
          } else {
            setErrMsg("Non riusciamo ad avviare la richiesta di blocco. Riprova o chiamaci allo 011 283514.");
          }
          return;
        }
      }
      // Meta Lead (browser) on real success; same event_id as server CAPI.
      try {
        const _isSeller = mode === "sell";
        metaLead({
          eventId: submissionId,
          leadType: _isSeller ? "seller" : "buyer",
          contentIds: (!_isSeller && car && car.shopify_id != null) ? [String(car.shopify_id)] : undefined,
          contentName: (!_isSeller && car) ? `${car.brand} ${car.trim}`.trim() : undefined,
          value: (!_isSeller && car) ? _priceNum : undefined,
        });
      } catch (e) { /* tracking must never block the commercial flow */ }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Invio non riuscito. Riprova o chiamaci allo 011 283514.");
    }
  };

  const title = dialogTitle(mode);
  const subtitle = dialogSubtitle(mode, car);

  const inputCls = "w-full rounded-lg border border-[var(--cm-line)] bg-white px-4 py-3 text-[0.95rem] text-[var(--cm-ink)] outline-none transition-colors focus:border-[var(--cm-ink)]";
  const labelCls = "mb-1.5 block text-[0.78rem] font-medium text-[#4a4a45]";
  const submitLabel = paidBooking ? "Richiedi il blocco del veicolo" : "Invia richiesta";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-h-[92vh] max-w-[520px] gap-0 overflow-y-auto rounded-[18px] border-none bg-white p-0 text-[var(--cm-ink)] [&>button]:hidden">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="relative p-7">
          <button
            type="button"
            onClick={onClose}
            data-testid="lead-close"
            aria-label="Chiudi"
            className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[var(--cm-ink)] shadow-md ring-1 ring-black/10 transition-colors hover:bg-[var(--cm-bg)]"
          >
            <X size={18} />
          </button>

          {status === "success" ? (
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--cm-green)]">
                <Check size={26} className="text-white" strokeWidth={3} />
              </div>
              <h3 className="text-[1.4rem] font-semibold">Richiesta ricevuta</h3>
              <p className="mx-auto mt-3 max-w-[380px] text-[0.95rem] leading-relaxed text-[#6f6f68]">
                {successMessage(mode, form.name.split(" ")[0])}
              </p>
              <button onClick={onClose} className="mt-6 rounded-full bg-[var(--cm-ink)] px-6 py-3 text-[0.9rem] font-medium text-white">Chiudi</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p className="label-mono mb-2">{dialogEyebrow(mode)}</p>
              <h3 className="text-[1.5rem] font-semibold leading-tight">{title}</h3>
              <p className="mt-2 text-[0.9rem] text-[#6f6f68]">{subtitle}</p>

              {paidBooking && (
                <div className="mt-5 rounded-[12px] border border-[var(--cm-line)] bg-[var(--cm-bg)] p-4 text-[0.85rem]">
                  <div className="flex justify-between py-1"><span className="text-[#6f6f68]">Importo di prenotazione</span><span className="font-semibold">300 € · rimborsabile</span></div>
                  <div className="flex justify-between py-1"><span className="text-[#6f6f68]">Riserva</span><span className="font-semibold">sette giorni</span></div>
                  <p className="mt-2 text-[0.8rem] leading-relaxed text-[#7a7a72]">
                    Invii una richiesta di blocco: la richiesta da sola non blocca ancora il
                    veicolo. Carmaps verifica disponibilità e condizioni. Visione e prova su
                    appuntamento, presso la sede o nel luogo concordato. Se dopo la visione
                    decidi di non procedere, Carmaps dispone il rimborso il giorno stesso della
                    visione (i tempi di accredito seguono il metodo di pagamento).
                  </p>
                </div>
              )}

              <div className="mt-5 space-y-4">
                <div>
                  <label className={labelCls}>Nome e cognome*</label>
                  <input className={inputCls} placeholder="Es. Mario Rossi" value={form.name} onChange={set("name")} data-testid="lead-name-input" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Telefono</label>
                    <input className={inputCls} placeholder="Es. 333 123 4567" value={form.phone} onChange={set("phone")} data-testid="lead-phone-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Email{paidBooking ? "*" : ""}</label>
                    <input className={inputCls} placeholder="Es. mario@email.it" value={form.email} onChange={set("email")} data-testid="lead-email-input" />
                  </div>
                </div>
                <p className="text-[0.75rem] text-[#7a7a72]">Indica almeno un recapito: telefono o email obbligatorio.</p>
                {mode === "info" && car && (
                  <div>
                    <label className={labelCls}>Come preferisci essere ricontattato</label>
                    <select className={inputCls} value={form.channel} onChange={set("channel")} data-testid="lead-channel-select">
                      <option value="Telefono">Telefono</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Email">Email</option>
                    </select>
                  </div>
                )}
                {mode === "booking" && (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>Città</label>
                        <input className={inputCls} placeholder="Es. Torino" value={form.city} onChange={set("city")} data-testid="lead-city-input" />
                      </div>
                      <div>
                        <label className={labelCls}>Quando vorresti visionarla</label>
                        <input className={inputCls} placeholder="Es. questo weekend" value={form.viewingTime} onChange={set("viewingTime")} data-testid="lead-viewing-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>Modalità d'acquisto</label>
                        <select className={inputCls} value={form.payment} onChange={set("payment")} data-testid="lead-payment-select">
                          <option value="">Da valutare</option>
                          <option value="Finanziamento">Finanziamento</option>
                          <option value="Unica soluzione">Pagamento in unica soluzione</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Permuta</label>
                        <select className={inputCls} value={form.tradeIn} onChange={set("tradeIn")} data-testid="lead-tradein-select">
                          <option value="">Da valutare</option>
                          <option value="Sì">Sì, ho un'auto da permutare</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}
                {mode === "sell" && (
                  <>
                    <div>
                      <label className={labelCls}>Marca e modello*</label>
                      <input className={inputCls} placeholder="Es. Fiat Panda 1.2" value={form.vehicle} onChange={set("vehicle")} data-testid="lead-vehicle-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Anno</label>
                        <input className={inputCls} placeholder="Es. 2019" value={form.year} onChange={set("year")} />
                      </div>
                      <div>
                        <label className={labelCls}>Chilometri</label>
                        <input className={inputCls} placeholder="Es. 80.000" value={form.km} onChange={set("km")} />
                      </div>
                    </div>
                  </>
                )}
                <div>
                  <label className={labelCls}>{mode === "sell" ? "Note (facoltative)" : "Messaggio (facoltativo)"}</label>
                  <textarea className={inputCls} rows={3} placeholder="Scrivici pure qui…" value={form.message} onChange={set("message")} />
                </div>
                {/* honeypot */}
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              </div>

              {paidBooking && (
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-[0.82rem] text-[#4a4a45]">
                  <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
                  <span>Ho letto e accetto le condizioni di prenotazione e il trattamento dei dati secondo la <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[var(--cm-ink)]">Privacy Policy</a>.</span>
                </label>
              )}

              {(errMsg || status === "error") && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#fdecec] px-3 py-2 text-[0.85rem] text-[#b42318]">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{errMsg}</span>
                </div>
              )}

              {!paidBooking && (
                <p className="mt-3 text-[0.72rem] text-[#9a9a92]">
                  Inviando accetti di essere ricontattato. Trattiamo i dati secondo la{" "}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[var(--cm-ink)]">Privacy Policy</a>.
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="lead-submit-btn"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--cm-ink)] px-6 py-3.5 text-[0.92rem] font-medium text-white transition-transform hover:scale-[1.01] disabled:opacity-60 [&_svg]:text-white"
              >
                {status === "loading"
                  ? (<><Loader2 size={16} className="animate-spin" /> Invio in corso…</>)
                  : submitLabel}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
